// hooks/useHomePage.js
import { useState, useEffect, useCallback, useReducer } from 'react';
import { useHistory } from 'react-router-dom';
import Store from 'store';
import apiClient from '../_utils/axios';
import store from '../store';
import { getCurrentLang } from '../libs/lang';

// 상수 정의
const RESPONSIVE_CONFIG = {
  main: { 0: { items: 1 } },
  category: {
    0: { items: 1 },
    700: { items: 2 },
    1050: { items: 3 },
    1400: { items: 4 },
  },
};

// 초기 상태
const initialState = {
  // UI 상태
  ui: {
    showbasket: false,
    showfavmodal: false,
  },
  // 데이터 상태
  data: {
    mainBannerListKo: [],
    mainBannerListEn: [],
    mainBannerListJp: [],
    homeBannerListKo: [],
    homeBannerListEn: [],
    homeBannerListJp: [],
    homePartnerList: [],
    noticeListKo: [],
    noticeListEn: [],
    keywordList: [],
  },
  // 설정 상태
  config: {
    main_title_1_ko: '',
    main_title_2_ko: '',
    main_title_3_ko: '',
    main_title_4_ko: '',
    main_title_5_ko: '',
    main_title_6_ko: '',
    main_title_1_en: '',
    main_title_2_en: '',
    main_title_3_en: '',
    main_title_4_en: '',
    main_title_5_en: '',
    main_title_6_en: '',
    main_title_1_jp: '',
    main_title_2_jp: '',
    main_title_3_jp: '',
    main_title_4_jp: '',
    main_title_5_jp: '',
    main_title_6_jp: '',
  },
  // 사용자 상태
  user: {
    user_uuid: '',
    is_tooked_survey: true,
  },
};

// 리듀서
const homePageReducer = (state, action) => {
  switch (action.type) {
    case 'SET_UI':
      return {
        ...state,
        ui: { ...state.ui, ...action.payload },
      };
    case 'SET_DATA':
      return {
        ...state,
        data: { ...state.data, ...action.payload },
      };
    case 'SET_CONFIG':
      return {
        ...state,
        config: { ...state.config, ...action.payload },
      };
    case 'SET_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

// API 서비스 분리
class HomePageService {
  static async getMainBannerList() {
    try {
      const [res_ko, res_en, res_jp] = await Promise.all([
        apiClient.get(`/web/banner?type=main&lang=ko`),
        apiClient.get(`/web/banner?type=main&lang=en`),
        apiClient.get(`/web/banner?type=main&lang=jp`),
      ]);

      return {
        mainBannerListKo: res_ko?.data || [],
        mainBannerListEn: res_en?.data || [],
        mainBannerListJp: res_jp?.data || [],
      };
    } catch (error) {
      console.error('Failed to fetch main banner list:', error);
      return {
        mainBannerListKo: [],
        mainBannerListEn: [],
        mainBannerListJp: [],
      };
    }
  }

  static async getHomeBannerList() {
    try {
      const lang = getCurrentLang() === 'kr' ? 'ko' : getCurrentLang();
      const res = await apiClient.get(`/web/banner?type=home&lang=${lang}`);

      switch (lang) {
        case 'ko':
          return { homeBannerListKo: res?.data || [] };
        case 'en':
          return { homeBannerListEn: res?.data || [] };
        case 'jp':
          return { homeBannerListJp: res?.data || [] };
        default:
          return { homeBannerListKo: res?.data || [] };
      }
    } catch (error) {
      console.error('Failed to fetch home banner list:', error);
      return {};
    }
  }

  static async getConfig() {
    try {
      const res = await apiClient.get('/web/config');
      return res?.data || {};
    } catch (error) {
      console.error('Failed to fetch config:', error);
      return {};
    }
  }
}

// 커스텀 훅
export const useHomePage = () => {
  const [state, dispatch] = useReducer(homePageReducer, initialState);
  const history = useHistory();

  // 초기화
  useEffect(() => {
    const initializePage = async () => {
      document.body.scrollTop = 0;

      // 병렬로 데이터 로딩
      const [mainBanners, homeBanners, config] = await Promise.all([
        HomePageService.getMainBannerList(),
        HomePageService.getHomeBannerList(),
        HomePageService.getConfig(),
      ]);

      dispatch({ type: 'SET_DATA', payload: { ...mainBanners, ...homeBanners } });
      dispatch({ type: 'SET_CONFIG', payload: config });
    };

    initializePage();
  }, []);

  // 이메일 인증 체크
  useEffect(() => {
    const accessToken = Store.get('accessToken');
    const isEmailVerified = store.getState().is_email_verified;

    if (accessToken && !isEmailVerified) {
      history.push('/auth/email/register');
    }
  }, [history]);

  // UI 액션들
  const actions = {
    setUI: useCallback((payload) => {
      dispatch({ type: 'SET_UI', payload });
    }, []),

    setData: useCallback((payload) => {
      dispatch({ type: 'SET_DATA', payload });
    }, []),

    closeBasketModal: useCallback(() => {
      dispatch({ type: 'SET_UI', payload: { showbasket: false } });
    }, []),

    showBasketModal: useCallback(() => {
      dispatch({ type: 'SET_UI', payload: { showbasket: true } });
    }, []),

    goBasket: useCallback(() => {
      history.push('/mypage/purchase/basket');
    }, [history]),

    goBannerUrl: useCallback(
      (url, event) => {
        const urlRegex = /^(http|https):\/\//;

        if (event !== null) {
          history.push(`/events/view/${event}`);
        } else {
          const httpUrlCheck = urlRegex.test(url);
          if (httpUrlCheck) {
            window.open(url, '_blank');
          } else {
            history.push(url);
          }
        }
      },
      [history],
    ),

    goDesigner: useCallback(
      (id) => {
        history.push(`/designer/shop/${id}`);
      },
      [history],
    ),
  };

  return {
    state,
    actions,
    responsiveConfig: RESPONSIVE_CONFIG,
  };
};
